import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumTranslationKey } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import { createTableActionsColumn, formatTZLocal, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { FILTER_FIELD_SIZES, registerModuleFilters } from '@/utils/input/filters';

import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const CreatedDateFilter = {
  ...Filters.DateFilter,
  label: 'global.text.createdDate',
};

const TraceActorFilter = {
  ...BaseFilters.EnumFilter,
  name: 'actor',
  label: 'traces.text.actor',
  enumNamespace: getEnumNamespace(CoreEnums.TraceActor),
  enumSource: CoreEnums.TraceActor,
  options: createEnumOptions(CoreEnums.TraceActor, 'traces.actor'),
};

const TraceOperationFilter = {
  ...BaseFilters.EnumFilter,
  name: 'operation',
  label: 'traces.text.operation',
  isEnum: false,
  needsQuotes: true,
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
  options: createEnumOptions(CoreEnums.TraceOperation, 'traces.operation', { casing: 'camel' }),
  filterSearch: true,
};

const IsSuccessfulOperationFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'isSuccessfulOperation',
  label: 'traces.text.isSuccessfulOperation',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'error' },
  },
  badgeType: 'group-item',
};

export const filtersConfig = [
  CreatedDateFilter,
  TraceActorFilter,
  TraceOperationFilter,
  IsSuccessfulOperationFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

export function mapTraceDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.name !== 'isSuccessfulOperation') return row;

    const isSuccessful = item.isSuccessfulOperation === true;
    return {
      ...row,
      type: 'badge',
      badgeType: 'group-item',
      badgeStatus: isSuccessful ? 'success' : 'error',
      value: isSuccessful ? 'global.text.yes' : 'global.text.no',
      locale: true,
    };
  });
}

export function buildTraceColumns({ formatMessage, openDrawerCreateOrEdit, searchCriteria }) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'created',
      title: formatMessage({ id: 'global.created' }),
      dataIndex: 'created',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : null),
    },
    {
      key: 'actor',
      title: formatMessage({ id: 'traces.text.actor' }),
      dataIndex: 'actor',
      sorter: true,
      align: 'center',
      render: (value) => {
        if (value === undefined || value === null) {
          return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
        }
        const translationKey = getEnumTranslationKey(CoreEnums.TraceActor, value, 'traces.actor');
        const textCell = formatMessage({ id: translationKey }) || DEFAULT_EMPTY_VALUE;
        return <Badge status="default" type="process" text={textCell} />;
      },
    },
    {
      key: 'environmentName',
      title: formatMessage({ id: 'traces.text.environment' }),
      dataIndex: 'environmentName',
      sorter: true,
      render: highlight,
    },
    {
      key: 'user',
      title: formatMessage({ id: 'global.text.user' }),
      dataIndex: 'user',
      sorter: true,
      render: highlight,
    },
    {
      key: 'isSuccessfulOperation',
      title: formatMessage({ id: 'traces.text.isSuccessfulOperation' }),
      dataIndex: 'isSuccessfulOperation',
      sorter: true,
      align: 'center',
      render: (value) => {
        const textCell = formatMessage({ id: value ? 'global.text.yes' : 'global.text.no' });
        return <Badge text={textCell} status={value ? 'success' : 'error'} type="group-item" />;
      },
    },
    {
      key: 'operation',
      title: formatMessage({ id: 'global.text.operation' }),
      dataIndex: 'operation',
      sorter: true,
      align: 'center',
      render: (value) => {
        if (!value) return null;
        const translationKey = getEnumTranslationKey(
          CoreEnums.TraceOperation,
          value,
          'traces.operation',
        );
        const textCell = formatMessage({ id: translationKey }) || value;
        return <Badge status="default" type="process" text={textCell} />;
      },
    },
    {
      key: 'entity',
      title: formatMessage({ id: 'traces.text.entity' }),
      dataIndex: 'entity',
      sorter: true,
      render: highlight,
    },
    {
      key: 'entityId',
      title: formatMessage({ id: 'traces.text.entityId' }),
      dataIndex: 'entityId',
      sorter: true,
      render: highlight,
    },
    {
      key: 'observations',
      title: formatMessage({ id: 'global.text.observation' }),
      dataIndex: 'observations',
      sorter: true,
      render: highlight,
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (el) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(el.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
